import React, {
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { bind } from 'bind-event-listener';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { NATIVE_EMBED_EXTENSION_KEY } from '@atlaskit/editor-common/extensions';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { getLocalIdsFromSelection } from '@atlaskit/editor-common/selection';
import type { ExtractInjectionAPI, SelectionContext } from '@atlaskit/editor-common/types';
import type { AdditionalNodeContextFragment } from '@atlaskit/editor-common/types/selection';
import { ReactEditorViewContext } from '@atlaskit/editor-common/ui-react';
import type { JSONNode } from '@atlaskit/editor-json-transformer/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import { publish, useSubscribe } from '@atlaskit/rovo-triggers/main';
import { Topics, type RovoRemixPreset } from '@atlaskit/rovo-triggers/types';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { token } from '@atlaskit/tokens';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { startRemixCreateExperience } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { remixReadyMessages } from '@atlassian/malleable-ui-components/remix/remix-ready-toast';
import { RemixThinkingBar } from '@atlassian/malleable-ui-components/remix/remix-thinking-bar';
import type {
	AddRemixHistoryEntryPayload,
	AddRemixHistoryStackEntryPayload,
	RemixFinalizeFailedPayload,
	RemixTypeProps,
} from '@atlassian/malleable-ui-components/types';
import { RovoThinkingBarWrapper } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBarWrapper';
import { RovoThinkingReady } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingReady';

import { makeAddRemixHistoryEntryCommand } from '../../editor-commands/addRemixHistoryEntry';
import { makeAddRemixHistoryStackEntryCommand } from '../../editor-commands/addRemixHistoryStackEntry';
import { clearRemixThinkingDecoration } from '../../editor-commands/clear-remix-thinking-decoration';
import { markRemixWhiteboardNeedsSpaceSelection } from '../../editor-commands/mark-remix-whiteboard-needs-space-selection';
import { openMauiEmbedSpacePickerCommand } from '../../editor-commands/maui-embed-space-picker';
import { setRemixThinkingComplete } from '../../editor-commands/set-remix-thinking-complete';
import { setShowLocationPickerEditorCommand } from '../../editor-commands/set-show-location-picker';
import { onStartRemixThinking } from '../../editor-commands/start-remix-thinking';
import type { MalleableUiPlugin, PluginConfiguration } from '../../malleableUiPluginType';
import {
	MauiDiagramPasteFlagType,
	MauiEmbedFinalizeStatus,
} from '../../pm-plugins/maui-diagram-paste-plugin';
import { getRemixThinkingWidgetContainer } from '../../pm-plugins/remix-thinking/remix-thinking-decoration';
import { useThinkingWidgetViewportPlacement } from '../hooks/useIsThinkingWidgetInViewport';
import { useRemixDiagramReadySignal } from '../hooks/useRemixDiagramReadySignal';
import { useRemixImageReadySignal } from '../hooks/useRemixImageReadySignal';
import { messages } from '../messages';
import {
	cancelRemixThinking,
	getAbortRequest,
	onRemixThinkingCompleteAnimationFinished,
	registerAbortRequest,
} from '../utils/remix-state';

type NativeContentContextFragment = AdditionalNodeContextFragment & {
	contentResult: Promise<unknown>;
	type: 'nativeContentContext';
};

type EmbeddedPageContent = {
	embeddedPageBody: string;
};

type NativeEmbedNode = JSONNode & {
	attrs: {
		extensionKey: string;
		localId: string;
	};
};

const isEmptyParagraphNode = (node: JSONNode): boolean => {
	return node.type === 'paragraph' && (node.content?.length ?? 0) === 0;
};

const isNativeEmbedNode = (node: JSONNode): node is NativeEmbedNode => {
	const { attrs } = node;

	return (
		node.type === 'extension' &&
		typeof attrs === 'object' &&
		attrs !== null &&
		'extensionKey' in attrs &&
		typeof attrs.extensionKey === 'string' &&
		attrs.extensionKey.startsWith(NATIVE_EMBED_EXTENSION_KEY) &&
		'localId' in attrs &&
		typeof attrs.localId === 'string' &&
		attrs.localId.length > 0
	);
};

const getNativeEmbedUrl = (node: PMNode): string | undefined => {
	const url = node.attrs.parameters?.macroParams?.url?.value;
	return typeof url === 'string' ? url : undefined;
};

const getTerminalUrlSegment = (url: string): string | undefined => {
	try {
		const pathname = new URL(url).pathname;
		return pathname.split('/').filter(Boolean).pop();
	} catch {
		return url.split('/').filter(Boolean).pop();
	}
};

export const isMatchingNativeEmbedUrl = ({
	nodeUrl,
	targetUrl,
}: {
	nodeUrl: string;
	targetUrl: string;
}): boolean => {
	if (nodeUrl === targetUrl) {
		return true;
	}

	const nodeId = getTerminalUrlSegment(nodeUrl);
	const targetId = getTerminalUrlSegment(targetUrl);
	return Boolean(nodeId && targetId && nodeId === targetId);
};

const resolveNativeEmbedScrollTargetByUrl = ({
	doc,
	targetUrl,
	editorView,
}: {
	doc: PMNode;
	editorView: NonNullable<React.ContextType<typeof ReactEditorViewContext>['editorView']>;
	targetUrl: string | undefined;
}): HTMLElement | null => {
	if (!targetUrl) {
		return null;
	}

	let targetPos: number | undefined;
	doc.descendants((node, pos) => {
		if (targetPos !== undefined) {
			return false;
		}
		const nodeUrl = getNativeEmbedUrl(node);
		if (
			node.type.name === 'extension' &&
			typeof node.attrs.extensionKey === 'string' &&
			node.attrs.extensionKey.startsWith(NATIVE_EMBED_EXTENSION_KEY) &&
			nodeUrl &&
			isMatchingNativeEmbedUrl({ nodeUrl, targetUrl })
		) {
			targetPos = pos;
			return false;
		}
		return true;
	});

	const targetDom = targetPos !== undefined ? editorView.nodeDOM(targetPos) : null;
	return targetDom instanceof HTMLElement ? targetDom : (targetDom?.parentElement ?? null);
};

const isNativeContentContextFragment = (
	context: AdditionalNodeContextFragment | undefined,
): context is NativeContentContextFragment => {
	return context?.type === 'nativeContentContext' && 'contentResult' in context;
};

const isEmbeddedPageContent = (content: unknown): content is EmbeddedPageContent => {
	return (
		typeof content === 'object' &&
		content !== null &&
		'embeddedPageBody' in content &&
		typeof content.embeddedPageBody === 'string'
	);
};

// JSON.parse succeeds on non-document payloads (`123`, `null`, `[]`), so the shape must be checked.
const isAdfDocument = (value: unknown): boolean => {
	return (
		typeof value === 'object' &&
		value !== null &&
		!Array.isArray(value) &&
		(value as { type?: unknown }).type === 'doc'
	);
};

const getRemixTypeProps = (selectedContext: SelectionContext): RemixTypeProps => {
	const blockProps = { remixType: 'block' } as const;

	if (!fg('maui_remixable_page_native_embeds')) {
		return blockProps;
	}

	// The AI selection context can include empty paragraph nodes adjacent to the
	// user-selected object. Ignore those placeholders so selecting a single native
	// embed plus surrounding empty paragraphs still enables object remix, while
	// any other selected content continues to fall back to block remix.
	const nodes = selectedContext.selectionFragment?.filter((node) => !isEmptyParagraphNode(node));

	if (nodes?.length !== 1) {
		return blockProps;
	}

	const [node] = nodes;
	if (!node || !isNativeEmbedNode(node)) {
		return blockProps;
	}

	const { localId } = node.attrs;
	const context = selectedContext.additionalNodeContext?.[localId];

	if (!isNativeContentContextFragment(context)) {
		return blockProps;
	}

	// A rejection here (or a rejected `contentResult`) is the read failure; the consumer handles it as a non-success.
	const adfDocument = context.contentResult.then((content) => {
		if (content === undefined) {
			throw new Error('Native embed content is unavailable');
		}

		if (!isEmbeddedPageContent(content) || content.embeddedPageBody === '') {
			throw new Error('Native embed content is missing an embedded page body');
		}

		let parsedBody;
		try {
			parsedBody = JSON.parse(content.embeddedPageBody);
		} catch {
			throw new Error('Native embed content is malformed');
		}

		if (!isAdfDocument(parsedBody)) {
			throw new Error('Native embed content is not a valid ADF document');
		}

		return parsedBody;
	});

	// The consumer only awaits this on option-select, so a read that fails earlier has no awaiter yet.
	// The inert handler prevents an unhandled rejection; the consumer's await still gets the rejection.
	void adfDocument.catch(() => undefined);

	return {
		remixType: 'object',
		adfDocument,
	};
};

const styles = cssMap({
	// Floats a bar horizontally-centred against the bottom edge of the viewport,
	// matching the position of the other floating thinking/ready bars (and the
	// legacy toast).
	floatingBarBottom: {
		position: 'fixed',
		bottom: token('space.200'),
		left: '50%',
		transform: 'translateX(-50%)',
		width: 'fit-content',
		// 400px matches the shared Rovo floating ready bar design minimum; no ADS sizing token maps to it.
		minWidth: '400px',
		zIndex: 600,
	},
});

// How long the floating "ready" bar stays visible while the remix is out of
// view before auto-dismissing (per the Remix Figma spec).
const READY_BAR_AUTO_DISMISS_MS = 10000;

// Operational event fired when diagram board tracking gives up (custom event — not part of the
// standard analytics union, so it's passed via the `'customEventType'` generic below).
type ReadySignalBoardTimeoutEvent = {
	action: 'timedOut';
	actionSubject: 'mauiDiagram';
	actionSubjectId: 'readySignalBoardTracking';
	attributes: { contentId: string | undefined };
	eventType: EVENT_TYPE.OPERATIONAL;
};

interface RemixContentProps {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	config: PluginConfiguration;
}

type PendingRovoRemixTarget = {
	localIds: string[];
	preset: RovoRemixPreset;
};

export const RemixContent = ({ api, config }: RemixContentProps): React.JSX.Element | null => {
	const { createRemixContentComponent } = config;
	const { editorView } = useContext(ReactEditorViewContext);
	const { formatMessage } = useIntl();
	const pendingRovoRemixTargetRef = useRef<PendingRovoRemixTarget | undefined>(undefined);

	const captureRovoRemixTarget = (preset: RovoRemixPreset) => {
		pendingRovoRemixTargetRef.current = {
			preset,
			localIds:
				preset === 'selection' && editorView
					? (getLocalIdsFromSelection(editorView.state.selection) ?? [])
					: [],
		};
	};

	useSubscribe({ topic: Topics.AI_MATE }, (payload) => {
		if (payload.type === 'open-rovo-remix-sidebar' && payload.data.remixConsumer === 'editor') {
			captureRovoRemixTarget(payload.data.remixPreset);
		}
	});

	useSubscribe({ topic: Topics.ROVO_REMIX }, (payload) => {
		if (payload.type === 'update-rovo-remix-preset' && payload.data.remixConsumer === 'editor') {
			captureRovoRemixTarget(payload.data.remixPreset);
		}
	});

	useSubscribe(
		{ topic: Topics.ROVO_REMIX, consumeOnceKey: 'editor-rovo-remix-submit' },
		(payload) => {
			if (
				payload.type !== 'submit-rovo-remix' ||
				payload.data.remixConsumer !== 'editor' ||
				UNSAFE_expValNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'control') !==
					'variant2'
			) {
				return;
			}

			const target = pendingRovoRemixTargetRef.current;
			pendingRovoRemixTargetRef.current = undefined;

			if (payload.data.remixPreset !== 'selection') {
				return;
			}
			if (target?.preset !== 'selection' || target.localIds.length === 0 || !api) {
				publish(Topics.ROVO_REMIX, {
					type: 'rovo-remix-handoff-failed',
					source: 'editor-rovo-remix',
				});
				return;
			}

			api.core.actions.execute(
				api.malleableUi.commands.startRemix({
					localIds: target.localIds,
					remixModalMetadata: {
						remixSource: 'rovoChat',
						preselectedType: payload.data.remixOption,
						preselectedSubType: payload.data.remixSubType,
						preselectedUserPrompt: payload.data.userPrompt,
						preselectedConversationId: payload.data.conversationId,
					},
				}),
			);
		},
	);

	const isViewMode = useSharedPluginStateWithSelector(
		api,
		['editorViewMode'],
		(states) => states.editorViewModeState?.mode === 'view',
	);

	const isModalOpen = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isRemixModalOpen || false,
	);

	const isThinking = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isThinking || false,
	);

	const isComplete = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isComplete || false,
	);

	const remixModalMetadata = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.remixModalMetadata,
	);

	const remixSource = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.remixSource,
	);

	const remixAttemptId = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.remixAttemptId,
	);

	const showLocationPicker = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.showLocationPicker || false,
	);

	const isUpdate = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isUpdate || false,
	);

	const historyEntryForUpdate = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.historyEntryForUpdate,
	);

	const dynamicUiSubtypeForUpdate = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.dynamicUiSubtypeForUpdate,
	);

	const savedSelection = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.savedSelection,
	);

	const remixUpdateScrollTargetUrl = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.remixUpdateScrollTargetUrl,
	);

	// Diagram-specific ready-signal logic (board tracking + scroll targeting) lives in its own hook.
	// When this isn't a diagram remix the returned values are inert.
	const { remixDiagramContentId, recordDiagramContentId, resolveDiagramScrollTarget } =
		useRemixDiagramReadySignal({ api, isThinking });

	// Image-specific ready-signal logic (media-node scroll target). Inert for non-image remixes.
	const { remixImageMediaId, resolveImageScrollTarget } = useRemixImageReadySignal({
		api,
		isThinking,
	});

	// Start Remix experience for floating toolbar entry point (can't use hooks in toolbar config).
	const experienceTracker = useAIMateExperienceTracker();
	const prevIsModalOpen = useRef(false);
	useEffect(() => {
		if (!prevIsModalOpen.current && isModalOpen && remixSource === 'floatingToolbar') {
			experienceTracker.start({
				name: ExperienceName.REMIX_START,
				id: ExperienceName.REMIX_START,
				attributes: {
					entryPoint: 'editor',
					remixSource: 'floatingToolbar',
					product: 'confluence',
				},
			});
			if (!isUpdate) {
				startRemixCreateExperience({
					experienceTracker,
					attributes: {
						entryPoint: 'editor',
						remixSource: 'floatingToolbar',
						product: 'confluence',
					},
				});
			}
		}
		prevIsModalOpen.current = isModalOpen;
	}, [isModalOpen, isUpdate, remixSource, experienceTracker]);

	const Component = useMemo(() => createRemixContentComponent(), [createRemixContentComponent]);
	// Prefer `startRemix`'s snapshot for Suggested Remix (experiment-gated) — the caret has already
	// moved off the anchor by the time this renders.
	const isSuggestedRemix =
		remixSource === 'suggestedRemix' &&
		UNSAFE_expValNoExposure('platform_editor_ai_content_suggested_edits', 'isEnabled', false);
	const isRovoChatRemix =
		remixSource === 'rovoChat' &&
		UNSAFE_expValNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'control') ===
			'variant2';
	const capturedSelectionContext =
		isSuggestedRemix || isRovoChatRemix ? remixModalMetadata?.capturedSelectionContext : undefined;
	const selectedContext =
		capturedSelectionContext ?? api?.aiExperience?.actions.getAISelectionContext();
	const remixTypeProps = useMemo(
		() => (selectedContext ? getRemixTypeProps(selectedContext) : undefined),
		[selectedContext],
	);

	const handleModalClose = useCallback(() => {
		api?.core.actions.execute(api?.malleableUi.commands.closeRemixModal());
	}, [api]);

	const onStartThinking = useCallback(
		(options?: { contentType?: string }) => {
			if (fg('platform_editor_ai_remix_toolbar')) {
				onStartRemixThinking({ api, options });
			} else {
				api?.aiExperience?.actions.toggleAiEntryPoints?.(true);
				api?.core.actions.execute(api?.malleableUi.commands.startRemixThinking(options));
			}
		},
		[api],
	);

	const onStopThinking = useCallback(
		(options?: { reopenModal?: boolean }) => {
			if (fg('platform_editor_ai_remix_toolbar')) {
				// Abort before cancelling so send-message-action.ts fires the paired
				// aiInteraction initiated + dismissed events via AbortError.
				getAbortRequest(api)?.();
				cancelRemixThinking({ api, reopenModal: options?.reopenModal });
			} else {
				api?.aiExperience?.actions.toggleAiEntryPoints?.(false);
				api?.core.actions.execute(api?.malleableUi.commands.stopRemixThinking(options));
			}
		},
		[api],
	);

	const onDismissThinkingUi = useCallback(
		(options?: { reopenModal?: boolean }) => {
			if (fg('platform_editor_ai_remix_toolbar')) {
				cancelRemixThinking({ api, reopenModal: options?.reopenModal });
			} else {
				api?.aiExperience?.actions.toggleAiEntryPoints?.(false);
				api?.core.actions.execute(api?.malleableUi.commands.stopRemixThinking(options));
			}
		},
		[api],
	);

	const onRegisterAbortRequest = useCallback(
		(abortFn: () => void) => registerAbortRequest(api, abortFn),
		[api],
	);

	const clearThinkingDecoration = useCallback(() => {
		api?.core.actions.execute(clearRemixThinkingDecoration());
	}, [api]);

	const replaceWhiteboardEmbed = useCallback(
		(options: { contentId: string; finalizedUrl: string; localId?: string }) => {
			api?.core.actions.execute(api?.malleableUi.commands.replaceWhiteboardEmbed(options));
		},
		[api],
	);

	// Maps the MAUI-side string status to the editor's finalize-status enum.
	const setMauiEmbedFinalizeStatus = useCallback(
		(options: { contentId: string; status: 'suppressed' | 'needs-finalize' }) => {
			api?.core.actions.execute(
				api?.malleableUi.commands.setMauiEmbedFinalizeStatus({
					contentId: options.contentId,
					status:
						options.status === 'suppressed'
							? isExperimentEnabled('cc_maui_polish_changes_batch_5')
								? MauiEmbedFinalizeStatus.STREAMING
								: MauiEmbedFinalizeStatus.CHECKING
							: MauiEmbedFinalizeStatus.NEEDS_FINALIZE,
				}),
			);
			// The 'suppressed' status is set as the diagram starts streaming in — record its
			// contentId so the ready-signal UI can target the board node.
			if (options.status === 'suppressed') {
				recordDiagramContentId(options.contentId);
			}
		},
		[api, recordDiagramContentId],
	);

	const setThinkingComplete = useCallback(
		(isComplete: boolean) => {
			// this is to clear the inline-Rovo streaming target when the remix is complete
			if (isComplete) {
				api?.aiExperience?.actions?.endInlineRovoStreaming?.();
			}
			api?.core.actions.execute(setRemixThinkingComplete(isComplete));
			if (expValEquals('cc_maui_polish_changes_batch_1', 'isEnabled', true)) {
				// View mode owns the floating thinking bar; let that bar finish its completion
				// animation and open the preview before stopping thinking.
				if (isComplete && !isViewMode) {
					onRemixThinkingCompleteAnimationFinished({ api });
				}
			} else {
				if (isComplete) {
					onRemixThinkingCompleteAnimationFinished({ api });
				}
			}
		},
		[api, isViewMode],
	);

	const _onRemixThinkingCompleteAnimationFinished = useCallback(() => {
		onRemixThinkingCompleteAnimationFinished({ api });
	}, [api]);

	const setShowLocationPicker = useCallback(
		(showLocationPicker: boolean) => {
			api?.core.actions.execute(setShowLocationPickerEditorCommand(showLocationPicker));
		},
		[api],
	);

	// When automatic finalization of a remixed whiteboard fails, open the shared
	// native-embed space picker (the same UI used by the paste / in-embed banner
	// flow) instead of the platform's remix-only picker. We dispatch two separate
	// transactions: first register the embed as NEEDS_SPACE_SELECTION (carrying
	// the "Choose where to save whiteboard" flag), then open the picker. Returns
	// `true` so the platform skips its own fallback flag + picker.
	const onFinalizeFailed = useCallback(
		({ contentId, originalUrl }: RemixFinalizeFailedPayload): boolean => {
			if (!api) {
				return false;
			}

			api.core.actions.execute(
				markRemixWhiteboardNeedsSpaceSelection({
					contentId,
					originalUrl,
					flag: {
						id: `maui-embed-finalize-space-picker-${contentId}`,
						type: MauiDiagramPasteFlagType.INFO,
						title: formatMessage(messages.flagFinalizeSpacePickerTitle),
						description: formatMessage(messages.flagFinalizeSpacePickerDescription),
					},
				}),
			);
			api.core.actions.execute(openMauiEmbedSpacePickerCommand({ contentId, originalUrl }));

			return true;
		},
		[api, formatMessage],
	);

	const addHistoryEntry = useCallback(
		(options: AddRemixHistoryEntryPayload) => {
			api?.core.actions.execute(makeAddRemixHistoryEntryCommand(options));
		},
		[api],
	);

	const addHistoryStackEntry = useCallback(
		(options: AddRemixHistoryStackEntryPayload) => {
			api?.core.actions.execute(makeAddRemixHistoryStackEntryCommand(options));
		},
		[api],
	);

	// Bridges the plugin's `trackVariantUpdateComplete` command into the
	// remix flow; `useOnComplete` fires it once the FE insertion settles.
	const onVariantUpdateComplete = useCallback(
		(id: string) => {
			if (!api?.malleableUi?.commands.trackVariantUpdateComplete) {
				return;
			}
			api.core.actions.execute(api.malleableUi.commands.trackVariantUpdateComplete({ id }));
		},
		[api],
	);

	// Shows the "Output adjusted" flag via editor-level plugin state, which
	// survives this component unmounting on completion.
	const onSubtypeMismatch = useCallback(() => {
		if (!api?.malleableUi?.commands.showSubtypeAdjustedFlag) {
			return;
		}
		api.core.actions.execute(api.malleableUi.commands.showSubtypeAdjustedFlag());
	}, [api]);

	// The whiteboard is inserted asynchronously, so it isn't in the document when the remix reports
	// "ready". Latch its mount so the scroll waits for it (else it lands on the pre-remix selection).
	const [isDiagramBoardReady, setIsDiagramBoardReady] = useState(false);
	const handleDiagramBoardReady = useCallback(() => {
		setIsDiagramBoardReady(true);
	}, []);
	// Reset the latch when a new remix begins so a prior remix's board can't trigger this one's scroll.
	useEffect(() => {
		if (isThinking) {
			setIsDiagramBoardReady(false);
		}
	}, [isThinking]);

	// Report when diagram board tracking gives up (the board never mounted) so we can measure how
	// often the ready-signal silently falls back to the shared behaviour. Fires at most once per remix.
	const handleDiagramBoardTimeout = useCallback(() => {
		api?.analytics?.actions.fireAnalyticsEvent<ReadySignalBoardTimeoutEvent, 'customEventType'>({
			action: 'timedOut',
			actionSubject: 'mauiDiagram',
			actionSubjectId: 'readySignalBoardTracking',
			eventType: EVENT_TYPE.OPERATIONAL,
			attributes: { contentId: remixDiagramContentId },
		});
	}, [api, remixDiagramContentId]);

	// Track where the inline thinking widget sits relative to the viewport so we
	// can float a bar at the matching edge (top/bottom) whenever it scrolls out
	// of view — during both the thinking phase and the completed "ready" phase.
	// Gated so we don't resolve DOM or bind scroll/resize listeners when the
	// in-editor loading feature is off.
	const floatingBarPlacement = useThinkingWidgetViewportPlacement({
		editorView,
		isActive: (isThinking || isComplete) && fg('platform_editor_ai_remix_in_editor_loading'),
		diagramContentId: remixDiagramContentId,
		// Once an image remix's upload lands, the in-view decision is about the media node rather
		// than the zero-height thinking anchor that sits just above it.
		imageMediaId: remixImageMediaId,
		onDiagramBoardReady: handleDiagramBoardReady,
		onDiagramBoardTimeout: handleDiagramBoardTimeout,
	});
	const isRemixReady =
		!isViewMode &&
		isComplete &&
		!isThinking &&
		!isModalOpen &&
		fg('platform_editor_ai_remix_in_editor_loading');

	const handleReadyToastView = useCallback(() => {
		if (editorView) {
			// For diagrams, scroll to the whiteboard node itself; null for other remixes or before
			// the board mounts, so we fall back to the anchor resolution below.
			const boardTarget = resolveDiagramScrollTarget(editorView);
			const updateTarget =
				isUpdate && expValEquals('cc_maui_polish_changes_batch_4', 'isEnabled', true)
					? resolveNativeEmbedScrollTargetByUrl({
							doc: editorView.state.doc,
							editorView,
							targetUrl: remixUpdateScrollTargetUrl,
						})
					: null;
			// Images are uploaded and inserted after the remix reports ready, so target the media node
			// once it has landed — by then the thinking anchor no longer marks the result.
			const imageTarget = boardTarget ? null : resolveImageScrollTarget(editorView);

			const container = getRemixThinkingWidgetContainer(editorView);
			const target =
				boardTarget ||
				updateTarget ||
				imageTarget ||
				(savedSelection?.from !== undefined && editorView.nodeDOM(savedSelection.from)) ||
				container ||
				(() => {
					try {
						if (!savedSelection?.to) {
							return null;
						}
						const resolved = editorView.state.doc.resolve(savedSelection.to);
						const anchorPos = resolved.depth >= 1 ? resolved.after(1) : savedSelection.to;
						const domAtPos = editorView.domAtPos(anchorPos);
						return domAtPos.node instanceof HTMLElement
							? domAtPos.node
							: domAtPos.node.parentElement;
					} catch {
						return null;
					}
				})();
			if (target instanceof HTMLElement) {
				// Leave breathing room above the board (below the sticky toolbar). scrollIntoView reads the
				// margin synchronously, so set it for this scroll then clear it — no lingering node mutation.
				const needsScrollMargin = target === boardTarget || target === imageTarget;
				if (needsScrollMargin) {
					target.style.scrollMarginTop = token('space.300');
				}
				target.scrollIntoView({
					behavior: 'smooth',
					block: expValEquals('cc_maui_polish_changes_batch_1', 'isEnabled', true)
						? 'start'
						: 'center',
				});
				if (needsScrollMargin) {
					target.style.scrollMarginTop = '';
				}
			}
		}
		// setThinkingComplete(false); do the job, but a reset could be more meaningful
		setThinkingComplete(false);
	}, [
		editorView,
		savedSelection?.from,
		savedSelection?.to,
		isUpdate,
		remixUpdateScrollTargetUrl,
		resolveDiagramScrollTarget,
		resolveImageScrollTarget,
		setThinkingComplete,
	]);

	const handleReadyToastClose = useCallback(() => {
		// setThinkingComplete(false); do the job, but a reset could be more meaningful
		setThinkingComplete(false);
	}, [setThinkingComplete]);

	// Diagrams must wait for the async board mount before scrolling; other remixes have their result
	// in place already, so they scroll as soon as ready.
	const isDiagramRemix =
		remixDiagramContentId !== undefined &&
		UNSAFE_expValNoExposure('cc_maui_1028_update_diagram_scroll_logic', 'isEnabled', false) ===
			true;
	const isScrollTargetReady = isRemixReady && (!isDiagramRemix || isDiagramBoardReady);

	// Only scroll on the rising edge of readiness — not on later 'in-view' transitions from the user
	// scrolling the result into view, which would hijack their scroll.
	const prevIsScrollTargetReadyRef = useRef(false);
	useEffect(() => {
		const justBecameReady = isScrollTargetReady && !prevIsScrollTargetReadyRef.current;
		prevIsScrollTargetReadyRef.current = isScrollTargetReady;

		if (
			!justBecameReady ||
			!editorView ||
			(isUpdate && expValEquals('cc_maui_polish_changes_batch_4', 'isEnabled', true)) ||
			floatingBarPlacement !== 'in-view' ||
			!expValEquals('cc_maui_polish_changes_batch_1', 'isEnabled', true)
		) {
			return;
		}

		const animationFrameId = requestAnimationFrame(handleReadyToastView);
		return () => cancelAnimationFrame(animationFrameId);
	}, [editorView, floatingBarPlacement, handleReadyToastView, isScrollTargetReady, isUpdate]);

	// handle escape key to stop remix thinking
	useEffect(() => {
		if (!fg('platform_editor_ai_remix_in_editor_loading')) {
			return;
		}

		if (!isThinking || !editorView) {
			return;
		}

		// focus editor first
		if (!editorView.hasFocus()) {
			editorView.focus();
		}

		return bind(editorView.dom, {
			type: 'keydown',
			listener: (e: KeyboardEvent) => {
				if (e.key === 'Escape') {
					e.stopPropagation();

					if (fg('platform_editor_ai_remix_toolbar')) {
						// Abort before cancelling so send-message-action.ts fires the paired
						// aiInteraction initiated + dismissed events via AbortError.
						getAbortRequest(api)?.();
						cancelRemixThinking({
							api,
							reopenModal: expValEquals('cc_maui_polish_changes_batch_2', 'isEnabled', true)
								? false
								: undefined,
						});
					} else {
						if (
							expValEqualsNoExposure(
								'cc_page_experiences_editor_image_generation',
								'isEnabled',
								true,
							)
						) {
							// If a pre-supplied userPrompt was cancelled (e.g. image generation flow),
							// call the onCancel callback instead of reopening the remix modal.
							const currentMetadata =
								api?.malleableUi?.sharedState.currentState()?.remixModalMetadata;
							const cancelledPrompt = currentMetadata?.userPrompt;
							const onCancel = currentMetadata?.onCancel;
							if (cancelledPrompt && onCancel) {
								onStopThinking({ reopenModal: false });
								onCancel();
								return;
							}
						}
						onStopThinking({
							reopenModal: expValEquals('cc_maui_polish_changes_batch_2', 'isEnabled', true)
								? false
								: fg('platform_editor_ai_remix_toolbar')
									? !isUpdate
									: true,
						});
					}
				}
			},
		});
	}, [isThinking, onStopThinking, editorView, api?.malleableUi.sharedState, isUpdate, api]);

	// When the remix modal overlay is open (isModalOpen),
	// set the editor user intent to 'overlayOpen' so the floating toolbar is suppressed
	// (it would otherwise appear above the overlay in Jira).
	// We intentionally do NOT suppress during the thinking-only state
	// — the user may still interact with the editor while the AI is thinking.
	// Reset to 'default' when the modal closes or the component unmounts.
	useLayoutEffect(() => {
		if (!fg('platform_editor_ai_remix_in_editor_loading')) {
			return;
		}
		if (!isModalOpen) {
			if (api?.userIntent?.sharedState.currentState()?.currentUserIntent === 'overlayOpen') {
				api?.core?.actions.execute(api?.userIntent?.commands.setCurrentUserIntent('default'));
			}
			return;
		}
		api?.core?.actions.execute(api?.userIntent?.commands.setCurrentUserIntent('overlayOpen'));
		return () => {
			api?.core?.actions.execute(api?.userIntent?.commands.setCurrentUserIntent('default'));
		};
	}, [isModalOpen, api]);

	// Auto-dismiss the floating "ready" bar after a fixed delay when the remix is
	// complete but out of view (per the Figma spec). Resetting thinking-complete
	// removes the bar; if the user scrolls back to their remix (placement becomes
	// 'in-view') the bar is hidden anyway and the timer is cleared.
	const isFloatingReadyBarVisible =
		!isViewMode &&
		isComplete &&
		!isThinking &&
		!isModalOpen &&
		floatingBarPlacement !== 'in-view' &&
		fg('platform_editor_ai_remix_in_editor_loading');
	// `remixImageMediaId` is a dep so the window restarts when an image remix's upload finally
	// lands: for images the bar appears before the media node exists, and dismissing it while the
	// result is still arriving would leave the user with no way back to it.
	useEffect(() => {
		if (!isFloatingReadyBarVisible) {
			return;
		}
		const timeoutId = setTimeout(() => {
			setThinkingComplete(false);
		}, READY_BAR_AUTO_DISMISS_MS);
		return () => {
			clearTimeout(timeoutId);
		};
	}, [isFloatingReadyBarVisible, setThinkingComplete, remixImageMediaId]);

	if (!api || !Component || !selectedContext || !remixTypeProps) {
		return null;
	}

	// when the remix isComplete and
	// - the modal is not open because isComplete may not be reset to false when the modal is open
	// - not thinking because we need to show the thinking bar when isThinking and isComplete
	//   - after onCompleteAnimationFinished, isThinking will be false and only isComplete will be true
	// we need to show the ready toast
	if (isRemixReady) {
		// Surface the shared Rovo "ready" state as a floating bar at the edge of the
		// viewport (closing the inline thinking bar). Only show it when the inline
		// widget is scrolled out of view so it can take the user back to their remix.
		if (floatingBarPlacement === 'in-view') {
			return null;
		}
		return (
			<Box xcss={styles.floatingBarBottom}>
				<RovoThinkingBarWrapper>
					<RovoThinkingReady
						onView={handleReadyToastView}
						onClose={handleReadyToastClose}
						readyText={formatMessage(
							isUpdate
								? remixReadyMessages.updateReady
								: // cc_maui_create_keyword: when enabled, swap the "Remix" keyword for "Create" so the
									// ready toast doesn't leak the legacy copy. Use the no-exposure variant since the
									// create-keyword exposure has already fired upstream when the remix flow started.
									expValEqualsNoExposure('cc_maui_create_keyword', 'isEnabled', true)
									? remixReadyMessages.contentReady
									: remixReadyMessages.remixReady,
						)}
					/>
				</RovoThinkingBarWrapper>
			</Box>
		);
	}

	// When the flag is ON, only show when isModalOpen, isThinking, or showLocationPicker — this
	// is the correct gating behaviour for the new loading path.
	// When the flag is OFF (legacy path), always show — EXCEPT when a pre-supplied userPrompt
	// is present (image-gen flow) and the flow is done (neither isModalOpen nor isThinking),
	// to prevent the option-picker from flashing on cancellation.
	const hasPreSuppliedPrompt = !!remixModalMetadata?.userPrompt;
	const canShow =
		isModalOpen ||
		isThinking ||
		showLocationPicker ||
		(!fg('platform_editor_ai_remix_in_editor_loading') && !hasPreSuppliedPrompt);

	// While the remix is generating, surface a floating thinking bar at the edge
	// of the viewport whenever the inline thinking widget is scrolled out of view,
	// so the user stays aware the process is ongoing. It sticks to the bottom when
	// the widget is above the viewport, or the top when it is below. Rendered
	// alongside the modal/option-picker Component (not instead of it) so any
	// underlying streaming/staging state is never unmounted.
	//
	// During an update (edit of an existing remix), the RemixContainer inside
	// `Component` already renders its own fixed thinking bar (its `hideThinkingBar`
	// guard is `isThinking && !isUpdate`, so it is only hidden for fresh inserts).
	// Rendering this floating bar as well would produce two overlapping, slightly
	// offset bars, so it is gated off for updates to keep a single loading bar.
	const showFloatingThinkingBar =
		!isViewMode &&
		isThinking &&
		!isUpdate &&
		!isModalOpen &&
		floatingBarPlacement !== 'in-view' &&
		fg('platform_editor_ai_remix_in_editor_loading');

	const floatingThinkingBar = showFloatingThinkingBar ? (
		<Box xcss={styles.floatingBarBottom}>
			<RemixThinkingBar
				remixAttemptId={remixAttemptId}
				loadingBarType="mini-bar"
				isRelative
				onStop={() =>
					onStopThinking({
						reopenModal: expValEquals('cc_maui_polish_changes_batch_2', 'isEnabled', true)
							? false
							: !isUpdate,
					})
				}
			/>
		</Box>
	) : null;

	if (!canShow) {
		return floatingThinkingBar;
	}

	return (
		<React.Fragment>
			{floatingThinkingBar}
			<Component
				{...remixTypeProps}
				remixAttemptId={remixAttemptId}
				isViewMode={fg('platform_editor_ai_remix_toolbar') ? isViewMode : undefined}
				isModalOpen={isModalOpen}
				isThinking={isThinking}
				isUpdate={fg('platform_editor_ai_remix_toolbar') ? isUpdate : undefined}
				historyEntryForUpdate={
					fg('platform_editor_ai_remix_toolbar') ? historyEntryForUpdate : undefined
				}
				dynamicUiSubtypeForUpdate={
					fg('platform_editor_ai_remix_toolbar') ? dynamicUiSubtypeForUpdate : undefined
				}
				isComplete={fg('platform_editor_ai_remix_in_editor_loading') ? isComplete : undefined}
				handleModalClose={handleModalClose}
				remixSource={remixSource}
				selectedContext={selectedContext}
				editorApi={api}
				clearThinkingDecoration={
					fg('platform_editor_ai_remix_in_editor_loading') ? clearThinkingDecoration : undefined
				}
				replaceWhiteboardEmbed={
					fg('platform_editor_ai_remix_in_editor_loading') ? replaceWhiteboardEmbed : undefined
				}
				setMauiEmbedFinalizeStatus={
					fg('platform_editor_ai_remix_in_editor_loading') ? setMauiEmbedFinalizeStatus : undefined
				}
				setThinkingComplete={
					fg('platform_editor_ai_remix_in_editor_loading') ? setThinkingComplete : undefined
				}
				onStartThinking={onStartThinking}
				onDismissThinkingUi={onDismissThinkingUi}
				onStopThinking={onStopThinking}
				onRegisterAbortRequest={onRegisterAbortRequest}
				onRemixThinkingCompleteAnimationFinished={
					fg('platform_editor_ai_remix_toolbar')
						? _onRemixThinkingCompleteAnimationFinished
						: undefined
				}
				showLocationPicker={
					fg('platform_editor_ai_remix_in_editor_loading') ? showLocationPicker : false
				}
				setShowLocationPicker={
					fg('platform_editor_ai_remix_in_editor_loading') ? setShowLocationPicker : () => {}
				}
				onFinalizeFailed={
					fg('platform_editor_ai_remix_in_editor_loading') ? onFinalizeFailed : undefined
				}
				addHistoryEntry={fg('platform_editor_ai_remix_toolbar') ? addHistoryEntry : () => {}}
				addHistoryStackEntry={
					fg('platform_editor_ai_remix_toolbar') ? addHistoryStackEntry : () => {}
				}
				onVariantUpdateComplete={
					fg('platform_editor_ai_remix_toolbar') ? onVariantUpdateComplete : undefined
				}
				onSubtypeMismatch={fg('platform_editor_ai_remix_toolbar') ? onSubtypeMismatch : undefined}
				remixModalMetadata={remixModalMetadata}
				hiddenOptions={config.hiddenOptions}
			/>
		</React.Fragment>
	);
};
